import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import type { ProjectTabKey } from './projectLabels'

/**
 * Fanen i adressen.
 *
 * Parameteren hedder `fane` og ikke `tab`, fordi resten af de brugervendte
 * stier i appen også er danske. Den ligger her og ikke i `ProjectDetailPage`,
 * fordi både siden, fanerne og alle de steder, der bygger et link ind i et
 * projekt, skal være enige om navnet — de var det ikke før.
 */
export const TAB_PARAM = 'fane'

/**
 * Parametrene, der peger på ét element inde på en fane.
 *
 * Navnene er engelske som fanenøglerne (`fane=documents`), så adressen ikke
 * blander to sprog inden for samme forespørgsel.
 */
export const PROJECT_ITEM_PARAMS = [
  'documentId',
  'drawingId',
  'taskId',
  'defectId',
  'checklistId',
  'tenderId',
  'threadId',
] as const

export type ProjectItemParam = (typeof PROJECT_ITEM_PARAMS)[number]

/**
 * Adressen til ét element inde på et projekt.
 *
 * Bruges af beskeder og søgning. Begge byggede før deres egen streng med
 * `?tab=`, som ingen side læste, og landede derfor altid på overblikket.
 */
export function projectItemHref(
  projectId: string,
  tab: ProjectTabKey,
  param: ProjectItemParam,
  id: string,
): string {
  return `/app/projects/${projectId}?${TAB_PARAM}=${tab}&${param}=${id}`
}

/** Adressen til en fane uden et bestemt element. */
export function projectTabHref(projectId: string, tab: ProjectTabKey): string {
  return `/app/projects/${projectId}?${TAB_PARAM}=${tab}`
}

/**
 * Id'et på det element, fanen har åbent — i almindelig tilstand, spejlet i
 * adressen.
 *
 * <p>Erstatter en almindelig `useState` i hver faneflade. Forskellen er, at
 * det åbne element nu kan deles: en besked om en mangel, et søgetræf eller en
 * kopieret URL fører hen til netop den mangel og ikke til projektets forside.</p>
 *
 * <p><strong>Tilstanden er lokal, ikke adressen.</strong> Første forsøg læste
 * direkte fra `useSearchParams`, og så åbnede dialogen først efter en
 * navigation — et tik senere end klikket. Det er umærkeligt for et menneske,
 * men det er et kapløb: den, der oprettede noget og lukkede dialogen igen med
 * det samme, kunne nå at trykke, før den overhovedet var der. Så blev den
 * stående — og en modal dialog skjuler resten af siden for
 * tilgængelighedstræet. Med tilstanden lokal åbner dialogen i samme commit som
 * klikket, og adressen følger efter.</p>
 *
 * <p>Adressen kan også skifte uden om os — et beskedlink, et søgetræf,
 * browserens tilbage-knap. Derfor overtages parameteren, når den ændrer sig.</p>
 *
 * <p><strong>Men kun når den skifter uden om os.</strong> Vores egne
 * skrivninger er langsommere end tilstanden: `open(id)` sætter `openId` med det
 * samme og beder derefter routeren om at ændre adressen. Åbner og lukker man
 * hurtigt efter hinanden, når den første navigation frem *efter* lukningen —
 * og så gik `fromUrl` fra null til id, mens dialogen allerede var lukket, og
 * effekten åbnede den igen. Den blev stående og opsnappede klik, indtil den
 * anden navigation nåede frem.</p>
 *
 * <p>Det kostede fire e2e-tests i CI: tavlen kunne ikke betjenes, fordi en
 * dialog, testen havde lukket og bekræftet var væk, kom tilbage af sig selv.
 * Lokalt lander begge navigationer i samme frame, React samler dem, og
 * flimmeret findes ikke — det var kun synligt på en belastet maskine, hvilket
 * er den værste slags fejl at have i noget, alle syv faner bruger.</p>
 *
 * <p>Derfor holdes der styr på, hvad vi selv sidst bad om, og om vi stadig
 * venter på, at adressen når derhen. Kommer der en værdi, vi ikke har bedt om,
 * mens vi venter, er det ekkoet af en tidligere skrivning og ikke en
 * navigation.</p>
 *
 * <p>Skrives med `replace`, som faneskiftet gør. At åbne og lukke tre kort i
 * en liste er ikke tre steder at gå tilbage til — man ville skulle trykke
 * tilbage én gang pr. kort for at komme ud af projektet igen.</p>
 */
export function useOpenItemId(
  param: ProjectItemParam,
): [string | null, (id: string | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const fromUrl = searchParams.get(param)

  const [openId, setOpenId] = useState<string | null>(fromUrl)

  /** Det, vi sidst bad adressen om at være. */
  const intended = useRef<string | null>(fromUrl)

  /** Sandt, indtil adressen er nået frem til `intended`. */
  const awaitingUrl = useRef(false)

  useEffect(() => {
    if (fromUrl === intended.current) {
      // Adressen er nået frem. Herfra tæller ændringer igen som noget udefra.
      awaitingUrl.current = false
      return
    }

    // En værdi, vi ikke har bedt om, mens en af vores egne skrivninger stadig
    // er undervejs: det er ekkoet af den forrige, ikke en ny navigation.
    if (awaitingUrl.current) {
      return
    }

    intended.current = fromUrl
    setOpenId(fromUrl)
  }, [fromUrl])

  const open = useCallback(
    (id: string | null) => {
      intended.current = id
      awaitingUrl.current = true

      setOpenId(id)

      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current)

          if (id === null) {
            params.delete(param)
          } else {
            params.set(param, id)
          }

          return params
        },
        { replace: true },
      )
    },
    [param, setSearchParams],
  )

  return [openId, open]
}
